import { useMemo } from 'react'

import { F0Dialog, F0Form, f0FormField, useF0Form } from '@factorialco/f0-react'
import type { UseFileUpload } from '@factorialco/f0-react'
import { CreateManualTaskDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { multiEmployeeSelectorV2FormField } from 'modules/employees/components/EmployeeSelectorV2/formField'

import { useDirectUpload } from 'modules/core/hooks/useDirectUpload'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useActivity } from 'modules/core/lib/activities/activity'
import type { ActivityComponent } from 'modules/core/lib/activities/types'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

type Data = {
  employeeIds: string
  title: string
  description: string
  due_on: string
}

const useTaskFileUpload: UseFileUpload = () => {
  const { upload, progress, status, cancelUpload } = useDirectUpload({
    resourceType: 'Tasks::Task',
  })

  return {
    upload: async (file: File) => {
      const result = await upload(file)
      if (result.type === 'success') {
        return { type: 'success' as const, value: result.signedId }
      }
      return { type: 'aborted' as const }
    },
    progress,
    status,
    cancelUpload,
  }
}

export const CreateTaskActivity: ActivityComponent<Data> = ({
  employeeIds,
  title,
  description,
  due_on,
}) => {
  const { open, closeActivity } = useActivity()
  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const [createTask] = useMutation(CreateManualTaskDocument, {
    extractorFn: (data) => data.tasksMutations.createTasksInboxTask,
  })

  const assigneesField = useMemo(
    () =>
      multiEmployeeSelectorV2FormField({
        label: i18n.t('tasks.create_task.assignees'),
        section: '',
        mandatory: true,
        fieldConfig: {
          forcedFilters: { accessStatus: ['active'] },
          options: { disableSelectAll: true },
        },
      }),
    []
  )

  const schema = useMemo(
    () =>
      z.object({
        name: f0FormField(z.string().min(1), {
          label: i18n.t('tasks.create_task.title'),
          placeholder: i18n.t('tasks.create_task.title_placeholder'),
        }),
        content: f0FormField(z.string().optional(), {
          label: i18n.t('tasks.create_task.description'),
          placeholder: i18n.t('tasks.create_task.description_placeholder'),
          fieldType: 'textarea',
        }),
        dueOn: f0FormField(z.date().optional(), {
          label: i18n.t('tasks.create_task.due_on'),
        }),
        employeeIds: assigneesField,
        files: f0FormField(z.array(z.string()).optional(), {
          label: i18n.t('tasks.attached_files'),
          fieldType: 'file',
          multiple: true,
          useUpload: useTaskFileUpload,
        }),
      }),
    [assigneesField]
  )

  const defaultValues = useMemo(() => {
    const ids = employeeIds
      ? employeeIds
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      : []

    return {
      name: title || '',
      content: description || '',
      dueOn: due_on ? stringToDate(due_on, 'backend').toDate() : undefined,
      employeeIds: ids.length > 0 ? { type: 'manual' as const, ids } : undefined,
    }
  }, [employeeIds, title, description, due_on])

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    if (
      !data.employeeIds ||
      data.employeeIds.type !== 'manual' ||
      data.employeeIds.ids.length === 0
    ) {
      return {
        success: false as const,
        errors: {
          employeeIds: i18n.t('common.required'),
        },
      }
    }

    const selectedEmployeeIds = data.employeeIds.ids

    const result = await createTask({
      variables: {
        name: data.name,
        content: data.content || null,
        dueOn: data.dueOn ? i18nDateToString(data.dueOn, 'backend') : null,
        employeeIds: selectedEmployeeIds,
        files: data.files ?? [],
      },
    })

    let formErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      formErrors = errors
    })

    if (!handled.success) {
      return { success: false as const, errors: formErrors }
    }

    closeActivity()
    return { success: true as const }
  }

  return (
    <F0Dialog
      isOpen={open}
      title={i18n.t('tasks.create_task.dialog_title')}
      onClose={closeActivity}
      primaryAction={{
        label: i18n.t('tasks.create_task.create'),
        onClick: submit,
        disabled: isSubmitting || hasErrors,
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: closeActivity,
      }}
    >
      <F0Form
        formRef={formRef}
        name="create-inbox-task"
        schema={schema}
        defaultValues={defaultValues}
        submitConfig={{ type: 'default', hideSubmitButton: true }}
        onSubmit={handleSubmit}
      />
    </F0Dialog>
  )
}
